import { Building2, Loader2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Badge } from '@/shared/ui/badge';
import { useAuth } from '@/core/AuthContext';
import { useTenantStore, useTenantChange, useUserTenants, useAllTenants } from '@/core/hooks/useTenant';
import { type Tenant } from '@/core/api';

interface TenantSelectorProps {
  onlyWhenMultiple?: boolean;
}

export const TenantSelector = ({ onlyWhenMultiple = false }: TenantSelectorProps) => {
  const { profile } = useAuth();
  const { selectedTenantId, isTransitioning } = useTenantStore();
  const { changeTenant } = useTenantChange();
  const { data: memberships = [], isLoading: membershipLoading } = useUserTenants();

  const { data: allTenants = [], isLoading: allLoading } = useAllTenants(!!profile?.is_super_admin);

  const tenants: Tenant[] = profile?.is_super_admin
    ? allTenants
    : memberships
        .filter((membership) => membership.status === 'approved')
        .map((membership) => ({
          id: membership.tenant?.id ?? membership.tenant_id,
          name: membership.tenant?.name ?? 'Unknown',
          code: membership.tenant?.code ?? 'UNKNOWN',
          logo_url: null,
          microsoft_tenant_id: null,
          settings: null,
          is_active: true,
          created_at: '',
          updated_at: '',
        }));

  const isLoading = profile?.is_super_admin ? allLoading : membershipLoading;

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm">Loading...</span>
      </div>
    );
  }

  if (tenants.length === 0) {
    return (
      <Badge variant="secondary" className="text-xs">
        No tenants assigned
      </Badge>
    );
  }

  if (onlyWhenMultiple && tenants.length <= 1) {
    return null;
  }

  const selectedTenant = tenants.find((tenant) => tenant.id === selectedTenantId);

  return (
    <Select value={selectedTenantId || ''} onValueChange={changeTenant} disabled={isTransitioning}>
      <SelectTrigger className="w-[200px] bg-sidebar-accent border-sidebar-border">
        <div className="flex items-center gap-2">
          {isTransitioning ? (
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
          ) : (
            <Building2 className="h-4 w-4 text-primary" />
          )}
          <SelectValue placeholder="Select tenant">
            {selectedTenant?.name || 'Select tenant'}
          </SelectValue>
        </div>
      </SelectTrigger>
      <SelectContent>
        {tenants.map((tenant) => (
          <SelectItem key={tenant.id} value={tenant.id}>
            <div className="flex items-center gap-2">
              <span>{tenant.name}</span>
              <Badge variant="outline" className="text-xs">
                {tenant.code}
              </Badge>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
